import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IconArrowLeft,
  IconTrash,
  IconReceipt,
  IconCopy,
  IconBuildingStore,
  IconDotsVertical,
  IconArrowsSplit2,
  IconTicket,
  IconLink,
  IconUserEdit,
  IconChevronDown,
  IconPhone,
  IconDeviceMobile,
  IconMail,
} from '@tabler/icons-react';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { DateField, SmartSelect, ActionMenu } from '../components/dropdown';
import type { SmartSelectOption, ActionMenuOption } from '../components/dropdown';
import { Chip } from '../components/chip';
import { FormField } from '../components/panel/FormField';
import { CARD_SX, PRIMARY_BTN_SX, CHECKBOX_SX } from '../styles/designSystem';
import { useLocation, useNavigate } from 'react-router-dom';
import { RCS_NETWORK_INSTALL_SKU } from '@techgitter/contracts';
import type {
  BundleDiscount,
  QuoteChangePayload,
  QuoteDetail,
  QuoteItem,
  QuoteStatus,
  QuoteType,
  RepairDeviceType,
  RepairService,
  Vendor,
} from '@techgitter/contracts';
import {
  fetchQuoteDetail,
  updateQuote,
  deleteQuote,
  createQuoteVariation,
} from '../services/quotesApi';
import { DuplicateQuoteDialog } from '../components/quotes/DuplicateQuoteDialog';
import { QuoteEditor } from '../components/quotes/QuoteEditor';
import { DesktopBuildEditor } from '../components/quotes/DesktopBuildEditor';
import { buildDesktopBuildDescription } from '../components/quotes/desktopBuildCategories';
import { QuoteHtmlPreviewDialog } from '../components/quotes/QuoteHtmlPreviewDialog';
import { GenerateInvoiceDialog } from '../components/quotes/GenerateInvoiceDialog';
import { GenerateTicketDialog } from '../components/quotes/GenerateTicketDialog';
import { LinkInvoiceDialog } from '../components/quotes/LinkInvoiceDialog';
import { LinkTicketDialog } from '../components/quotes/LinkTicketDialog';
import { EditCustomerDialog } from '../components/quotes/EditCustomerDialog';
import { DistiListingSearchDialog } from '../components/quotes/DistiListingSearchDialog';
import { SendToOrderingButton } from '../components/quotes/SendToOrderingButton';
import { RepairServicesEditor } from '../components/quotes/RepairServicesEditor';
import { RepairQuoteDeviceDetails } from '../components/quotes/RepairQuoteDeviceDetails';
import { JobSummaryEditor } from '../components/quotes/JobSummaryEditor';
import { getVendors } from '../services/vendorService';
import { useQuoteTermsTemplates } from '../hooks/useQuoteTermsTemplates';
import { useAppSettings } from '../hooks/useAppSettings';
import { useSignalREvent } from '../hooks/useSignalR';
import { optionLineSellDisplay } from '../utils/quotePricing';
import { applyEft } from '../utils/quoteEftPricing';
import './QuoteDetailPage.css';

const STATUS_OPTIONS: QuoteStatus[] = [
  'requested',
  'draft',
  'sent',
  'accepted',
  'declined',
  'expired',
];

// SmartSelect option arrays (unified dropdown system — §10).
const STATUS_SELECT_OPTIONS: SmartSelectOption[] = STATUS_OPTIONS.map((s) => ({
  value: s,
  label: s,
}));

const GST_SELECT_OPTIONS: SmartSelectOption[] = [
  { value: 'ex', label: 'Ex GST' },
  { value: 'inc', label: 'Inc GST' },
];

const DEVICE_TYPE_OPTIONS: {
  value: Exclude<RepairDeviceType, null>;
  label: string;
}[] = [
  { value: 'laptop', label: 'Laptop' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'all_in_one', label: 'All-in-One' },
];

// "Any" + the three device types, for the repair-quote device SmartSelect.
const DEVICE_SELECT_OPTIONS: SmartSelectOption[] = [
  { value: '', label: 'Any' },
  ...DEVICE_TYPE_OPTIONS,
];

// `replacement` is intentionally excluded from the dropdown — legacy
// rows that carry that value will still load and display, but new
// quotes use one of the four new computer sub-types instead.
const TYPE_OPTIONS: { value: QuoteType; label: string }[] = [
  { value: 'new_laptop', label: 'New Laptop' },
  { value: 'new_desktop', label: 'New Desktop' },
  { value: 'new_desktop_custom', label: 'New Desktop (Custom)' },
  { value: 'new_all_in_one', label: 'New All-in-One' },
  { value: 'repair', label: 'Repair Service' },
  { value: 'network_install', label: 'Network Installation' },
  { value: 'm365', label: 'M365 Onboarding' },
];

/**
 * Compose the default consolidated description from the marked
 * options' descriptions, joined on newlines in item-position order.
 * Mirrors the backend's `_build_consolidated_description` so the
 * Regenerate button produces identical text to a fresh-create seed.
 */
const buildConsolidatedDescription = (items: QuoteItem[]): string => {
  const sorted = [...items].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );
  const parts: string[] = [];
  for (const item of sorted) {
    if (!item.markedForInvoice) continue;
    const opt = item.options.find((o) => o.id === item.selectedOptionId);
    const desc = (opt?.description || '').trim();
    if (desc) parts.push(desc);
  }
  return parts.join('\n');
};

const fmtCurrency = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

/** A muted, icon-led customer contact link (tel: / mailto:) shown in the
 *  quote header so the quoter can reach the customer without leaving the
 *  quote. Stops click propagation so it doesn't trip any parent handlers. */
const ContactLink: React.FC<{
  Icon: React.ComponentType<{ size?: number; stroke?: number }>;
  href: string;
  label: string;
}> = ({ Icon, href, label }) => (
  <a
    href={href}
    onClick={(e) => e.stopPropagation()}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 12,
      color: '#4A90D9',
      textDecoration: 'none',
      minWidth: 0,
    }}
  >
    <Icon size={14} stroke={1.5} />
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
  </a>
);

/** Compact secondary/outlined header action button — accent text, hairline
 *  border, soft accent hover (design-system §9). */
const headerBtnSx = {
  fontSize: 13,
  fontWeight: 500,
  py: 0.5,
  px: 1.25,
  minHeight: 0,
  color: '#4A90D9',
  borderColor: '#c8d4e0',
  '&:hover': { borderColor: '#4A90D9', bgcolor: '#EDF5FF' },
  '& .MuiButton-startIcon': { mr: 0.5 },
} as const;

/** Today + `days`, formatted as a local "YYYY-MM-DD" string to match the
 *  `validUntil` date-input value (date-only, no timezone shift). */
const addDaysIso = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Configured quote validity window, defaulting to 7 days when the
 *  `quoteValidityDays` app setting is unset. */
const DEFAULT_QUOTE_VALIDITY_DAYS = 7;

export const QuoteDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // The app's MainLayout dispatches on pathname regex rather than declaring a
  // React Router route, so useParams() is empty here. Extract the number the
  // same way InvoiceDetailPage does.
  const number = location.pathname.match(/^\/quote\/(.+)$/)?.[1] ?? '';
  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [status, setStatus] = useState<QuoteStatus>('draft');
  const [type, setType] = useState<QuoteType>('new_laptop');
  const [consolidated, setConsolidated] = useState(false);
  const [consolidatedDescription, setConsolidatedDescription] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  // Slug of the assigned T&C template. Null means "use whichever template
  // is default at render time." Loaded from the quote row + sent on save.
  const [termsTemplateId, setTermsTemplateId] = useState<string | null>(null);
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [repairServices, setRepairServices] = useState<RepairService[]>([]);
  const [bundleDiscount, setBundleDiscount] = useState<BundleDiscount | null>(
    null,
  );
  const [deviceType, setDeviceType] = useState<RepairDeviceType>(null);
  // Which check-in draft job (device) this repair quote is for. Lets
  // the Device Details block resolve and render the right device when
  // the linked work item's check-in has multiple devices. Null means
  // "no device selected" — Device Details either auto-resolves (single
  // device) or shows the picker (multi-device).
  const [sourceCheckInDraftJobId, setSourceCheckInDraftJobId] = useState<
    string | null
  >(null);
  // Vendors used by the third-party vendor-quote picker on repair
  // services. Loaded once per page mount; the editor filters the list
  // down to active third-party providers on demand. Errors are silent —
  // the editor degrades to an empty dropdown.
  const [vendors, setVendors] = useState<Vendor[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    getVendors(controller.signal).then(setVendors).catch(() => {});
    return () => controller.abort();
  }, []);
  const [includeEftFee, setIncludeEftFee] = useState<boolean>(true);
  // The rate is editable per-quote so techs can match the sourcing
  // vendor's actual fee. Stored as a percentage string so we don't fight
  // number-formatting on every keystroke; the derived fraction
  // (`eftFeeRate`) is what flows to save, total math, and dialogs.
  const [eftFeeRatePct, setEftFeeRatePct] = useState<string>('1.11');
  const eftFeeRate = (() => {
    const parsed = Number(eftFeeRatePct);
    return Number.isFinite(parsed) && parsed > 0 ? parsed / 100 : 0;
  })();
  const [priceDisplayMode, setPriceDisplayMode] = useState<'ex' | 'inc'>('ex');
  // Stored as a fraction (0.115 = 11.5%) on the quote; the UI shows it as a
  // percentage so the tech doesn't have to do the conversion in their head.
  const [markupPct, setMarkupPct] = useState('11.5');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [linkInvoiceOpen, setLinkInvoiceOpen] = useState(false);
  const [linkTicketOpen, setLinkTicketOpen] = useState(false);
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  // "Convert" menu (Generate ticket / Generate invoice / Link existing invoice).
  const convertBtnRef = useRef<HTMLButtonElement>(null);
  const [convertMenuOpen, setConvertMenuOpen] = useState(false);
  const [supplierSearchOpen, setSupplierSearchOpen] = useState(false);
  // Copy actions (variation / duplicate).
  const copyBtnRef = useRef<HTMLButtonElement>(null);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const { settings: appSettings } = useAppSettings();
  // Editing the customer attached to the quote is admin-gated (matches the
  // server-side gate on PATCH /api/customers/{id}).
  const isAdmin = useCurrentUser()?.hasRole('Admin') ?? false;

  // Opening the preview on a still-draft quote re-anchors its validUntil to
  // today + the configured validity window (fallback 7 days), so a draft that
  // sat around shows a fresh expiry to the customer. Persists on next save.
  const handleOpenPreview = (): void => {
    if (status === 'draft' && !isLocked) {
      const days =
        appSettings && Number.isFinite(appSettings.quoteValidityDays as number)
          ? (appSettings.quoteValidityDays as number)
          : DEFAULT_QUOTE_VALIDITY_DAYS;
      setValidUntil(addDaysIso(days));
    }
    setPreviewOpen(true);
  };

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!number) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchQuoteDetail(number, signal);
        setQuote(data);
        setName(data.name);
        setStatus(data.status);
        setType(data.type);
        // Custom builds are always a single summary line (forced server-side);
        // self-heal the toggle for any legacy row that stored false.
        setConsolidated(
          data.type === 'new_desktop_custom' ? true : data.consolidated,
        );
        setConsolidatedDescription(data.consolidatedDescription ?? '');
        setValidUntil(data.validUntil ?? '');
        setNote(data.note ?? '');
        setTermsTemplateId(data.termsTemplateId ?? null);
        setItems(data.items);
        setRepairServices(data.repairServices ?? []);
        setBundleDiscount(data.bundleDiscount ?? null);
        setDeviceType(data.deviceType ?? null);
        setSourceCheckInDraftJobId(data.sourceCheckInDraftJobId ?? null);
        setIncludeEftFee(data.includeEftFee ?? true);
        const loadedRate = Number.isFinite(data.eftFeeRate)
          ? data.eftFeeRate
          : 0.0111;
        setEftFeeRatePct(
          (loadedRate * 100).toFixed(2).replace(/\.?0+$/, ''),
        );
        setPriceDisplayMode(data.priceDisplayMode ?? 'ex');
        setMarkupPct(
          Number.isFinite(data.markup)
            ? (data.markup * 100).toFixed(2).replace(/\.?0+$/, '')
            : '11.5',
        );
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        // Don't clear `loading` for an aborted request — a replacement load
        // is already in flight (StrictMode double-mount, or switching
        // quotes). Clearing it here would briefly render the "not found"
        // branch (loading=false, quote=null) before the new data lands.
        if (!signal?.aborted) setLoading(false);
      }
    },
    [number],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // A quote locks once it's invoiced (promotedAt) OR its line items are added
  // to a ticket (ticketGeneratedAt — set server-side only for a line-items
  // generate). Merely being LINKED to a ticket (link-existing, an empty
  // generate, or a quote created from a ticket) does NOT lock it — the quote
  // stays editable. `isPromoted` stays invoice-specific (drives the "Promoted
  // to invoice N" chip); `isLocked` gates all editing.
  const isPromoted = quote?.promotedAt != null;
  const isTicketGenerated = quote?.ticketGeneratedAt != null;
  const isLocked = isPromoted || isTicketGenerated;

  // A quote may be linked to at most ONE ticket, so the "Generate ticket" /
  // "Link existing ticket" options are only offered on a quote with no ticket
  // yet. A quote created from — or already linked/converted to — a ticket
  // carries a job link (cr001_job_id → sm4rtJobId); the backend refuses a
  // second ticket in QuoteService, and we hide the options here to match.
  const isTicketLinked = isTicketGenerated || quote?.sm4rtJobId != null;

  // Both conversions create their RS record asynchronously in the
  // orchestrator, so the invoice number (promote) / ticket number (generate
  // ticket) isn't known at request time — only the local stamp is. The
  // orchestrator fires quotes:changed once it has patched the quote with the
  // new link. Patch it in place so the chip appears the instant the record
  // exists, without a refetch (the event already carries the identifiers).
  useSignalREvent('quotes:changed', (data: unknown) => {
    const payload = data as QuoteChangePayload;
    if (
      payload?.action !== 'promoted' &&
      payload?.action !== 'ticket_generated'
    ) {
      return;
    }
    setQuote((prev) => {
      if (!prev) return prev;
      const matches =
        payload.quoteId === prev.id || payload.number === prev.number;
      if (!matches) return prev;
      if (payload.action === 'promoted') {
        return {
          ...prev,
          invoiceId: payload.invoiceId,
          invoiceNumber: payload.invoiceNumber,
          // promotedAt is already set from the post-promote reload, but stamp a
          // fallback so the chip's isPromoted gate holds even if this event
          // somehow arrives first.
          promotedAt: prev.promotedAt ?? new Date().toISOString(),
        };
      }
      // ticket_generated — the orchestrator created the ticket and back-patched
      // the quote's job link. Surface the linkage (the "Job #N" / "Converted to
      // ticket #N" chip). Do NOT stamp ticketGeneratedAt here: it's the LOCK
      // marker, meaning the quote's line items were added to a ticket. The
      // server sets it only for a line-items generate, and the post-generate
      // reload already reflects it. An empty ticket stays editable, so we must
      // leave this as the server value rather than forcing it here.
      return {
        ...prev,
        sm4rtJobId: payload.jobId,
        rsTicketNumber: payload.ticketNumber,
      };
    });
  });

  const handleSave = async (): Promise<boolean> => {
    if (!number) return false;
    setSaving(true);
    setError(null);
    try {
      const parsedMarkup = Number(markupPct);
      const markupFraction = Number.isFinite(parsedMarkup)
        ? parsedMarkup / 100
        : 0.115;
      const isRepair = type === 'repair';
      const isServiceShaped = type === 'repair' || type === 'network_install';
      const updated = await updateQuote(number, {
        name,
        status,
        type,
        consolidated,
        consolidatedDescription: consolidatedDescription || null,
        validUntil: validUntil || null,
        note: note || null,
        termsTemplateId,
        // Send the tree matching the current type; the other one stays
        // untouched on the server so a tech mid-flight type-change doesn't
        // wipe the unused side until they explicitly save it. Network
        // installs share the repair-services tree + bundle discount, but
        // not the repair-only device / check-in fields.
        ...(isServiceShaped
          ? {
              repairServices,
              bundleDiscount,
              ...(isRepair ? { deviceType, sourceCheckInDraftJobId } : {}),
            }
          : { items }),
        includeEftFee,
        // 0 (invalid input or "0%") is sent as the rate — the backend
        // treats 0 the same as toggling EFT off.
        eftFeeRate,
        priceDisplayMode,
        markup: markupFraction,
      });
      setQuote(updated);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Generate Invoice promotes from server-stored state, so any in-memory
  // edits (e.g. marking an item, picking an option) must be persisted first
  // — otherwise the dialog's preview looks right but the server rejects it.
  const handleGenerateInvoice = async () => {
    setConvertMenuOpen(false);
    const ok = await handleSave();
    if (ok) setGenerateOpen(true);
  };

  // Generate Ticket also projects line items from server-stored state, so
  // save first (same reason as Generate Invoice).
  const handleGenerateTicket = async () => {
    setConvertMenuOpen(false);
    const ok = await handleSave();
    if (ok) setTicketOpen(true);
  };

  // Link an existing invoice — no line-item projection, but persist any edits
  // first so they aren't lost when the quote locks on link.
  const handleLinkInvoice = async () => {
    setConvertMenuOpen(false);
    const ok = await handleSave();
    if (ok) setLinkInvoiceOpen(true);
  };

  // Bind the quote to an existing RS ticket (raised outside the quote flow).
  // Persist edits first so they aren't lost when the quote locks on link.
  const handleLinkTicket = async () => {
    setConvertMenuOpen(false);
    const ok = await handleSave();
    if (ok) setLinkTicketOpen(true);
  };

  const handleCreateVariation = async () => {
    if (!number) return;
    setCopyMenuOpen(false);
    setCopyBusy(true);
    setError(null);
    try {
      const created = await createQuoteVariation(number);
      navigate(`/quote/${created.number}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCopyBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!number) return;
    if (
      !window.confirm(
        'Delete this quote? This cannot be undone.',
      )
    )
      return;
    try {
      await deleteQuote(number);
      navigate('/quotes');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Show the progress state while loading, and also during the brief window
  // before the first fetch resolves (quote still null, no error yet) so we
  // never flash the "not found" branch mid-navigation.
  if (loading || (!quote && !error)) {
    return (
      <Box
        className="quote-detail"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography sx={{ fontSize: 14, color: '#888' }}>
          Getting quote {number}…
        </Typography>
      </Box>
    );
  }

  if (!quote) {
    return (
      <Box className="quote-detail">
        <Box className="quote-detail__inner">
          <button type="button" className="quote-detail__back" onClick={() => navigate('/quotes')}>
            <IconArrowLeft size={16} stroke={1.5} />
            Back to quotes
          </button>
          <Alert severity="error">{error ?? 'Quote not found.'}</Alert>
        </Box>
      </Box>
    );
  }

  const isRepairQuote = type === 'repair';
  // Network installs share the repair-services tree, editor, and total
  // math, but not the repair-only device-details / diagnostic UI.
  const isServiceShaped = type === 'repair' || type === 'network_install';
  // Custom desktop builds use the dedicated build-sheet editor and are always
  // consolidated to a single summary line (forced server-side); the toggle is
  // hidden and the summary composer uses the category-labelled build format.
  const isCustomDesktop = type === 'new_desktop_custom';

  // Diagnostic reimbursement is auto-derived server-side and now applies
  // to any quote whose source job collected a diag fee (repair OR
  // new-computer outcome) — same gate the backend uses. Contract stores
  // the gross customer-paid amount (inc-GST); divide by 1.1 when we
  // need the ex-GST equivalent for the header total.
  const diagReimbursement = quote.diagnosticReimbursement;
  const diagReimbursementExGst = diagReimbursement
    ? diagReimbursement.amount / 1.1
    : 0;

  // Sum per-line displayed values rather than summing ex-GST and grossing
  // once. Each `Math.round(ex * 1.1 * 100) / 100` is what the customer
  // sees on the resulting invoice line, so summing those matches the
  // invoice total exactly. Summing ex-GST and multiplying once drops
  // fractional cents (e.g. 1045.45 + 268.18 → *1.1 = 1444.993 → 1444.99
  // vs the expected 1150.00 + 295.00 = 1445.00).
  const round2 = (n: number): number => Math.round(n * 100) / 100;
  const toDisplay = (exGst: number): number =>
    priceDisplayMode === 'inc' ? round2(exGst * 1.1) : round2(exGst);

  // The EFT buffer is applied per positive line — each service, each marked
  // item — the way the backend bakes it at promotion. The bundle discount and
  // the diagnostics credit come off afterwards un-uplifted: `_bake_in_eft_fee`
  // skips every negative line, since a credit is a fixed amount off.
  const eft = (amount: number): number =>
    applyEft(amount, includeEftFee, eftFeeRate);
  const linesTotal = isServiceShaped
    ? repairServices.reduce(
        (acc, svc) =>
          acc +
          eft(
            svc.components.reduce((sum, c) => {
              // Non-accepted third-party rows are vendor-quote comparisons —
              // kept on the quote for audit but billed at $0. Same rule
              // the backend (_sum_repair_services_total) and the customer
              // preview enforce.
              if (
                c.kind === 'third_party' &&
                c.vendorQuoteStatus !== 'accepted'
              ) {
                return sum;
              }
              return (
                sum +
                (Number.isFinite(c.sellPrice) ? toDisplay(c.sellPrice) : 0)
              );
            }, 0),
          ),
        0,
      )
    : items.reduce((acc, item) => {
        if (!item.markedForInvoice || !item.selectedOptionId) return acc;
        const opt = item.options.find((o) => o.id === item.selectedOptionId);
        return acc + (opt ? eft(optionLineSellDisplay(opt, priceDisplayMode)) : 0);
      }, 0);

  const total = round2(
    linesTotal -
      (isServiceShaped ? toDisplay(bundleDiscount?.amount ?? 0) : 0) -
      toDisplay(diagReimbursementExGst),
  );

  return (
    <Box className="quote-detail">
      <Box className="quote-detail__inner">
        <button type="button" className="quote-detail__back" onClick={() => navigate('/quotes')}>
          <IconArrowLeft size={16} stroke={1.5} />
          Back to quotes
        </button>

        {/* Header card — identity + record actions */}
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.2 }}>
                {quote.customerName}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#888' }}>Quote {quote.number}</Typography>
              {(quote.customerPhone || quote.customerMobile || quote.customerEmail) && (
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ mt: 0.5, flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25 }}
                >
                  {quote.customerPhone && (
                    <ContactLink
                      Icon={IconPhone}
                      href={`tel:${quote.customerPhone}`}
                      label={quote.customerPhone}
                    />
                  )}
                  {quote.customerMobile && (
                    <ContactLink
                      Icon={IconDeviceMobile}
                      href={`tel:${quote.customerMobile}`}
                      label={quote.customerMobile}
                    />
                  )}
                  {quote.customerEmail && (
                    <ContactLink
                      Icon={IconMail}
                      href={`mailto:${quote.customerEmail}`}
                      label={quote.customerEmail}
                    />
                  )}
                </Stack>
              )}
            </Box>
            {quote.variationGroupId && (
              <Tooltip title="View all variations of this quote">
                <span>
                  <Chip
                    tone="info"
                    onClick={() =>
                      navigate(
                        `/quotes?variationGroupId=${encodeURIComponent(
                          quote.variationGroupId as string,
                        )}`,
                      )
                    }
                  >
                    <IconArrowsSplit2 size={14} stroke={1.5} />
                    Variation set
                  </Chip>
                </span>
              </Tooltip>
            )}
            {isPromoted && quote.invoiceNumber && (
              <Chip
                tone="success"
                onClick={() => navigate(`/invoice/${quote.invoiceNumber}`)}
              >
                Promoted to invoice {quote.invoiceNumber}
              </Chip>
            )}
            {isTicketGenerated && (
              <Chip
                tone="success"
                onClick={
                  quote.rsTicketNumber
                    ? () => navigate(`/ticket/${quote.rsTicketNumber}`)
                    : undefined
                }
              >
                <IconTicket size={14} stroke={1.5} />
                {quote.rsTicketNumber
                  ? `Converted to ticket #${quote.rsTicketNumber}`
                  : 'Converted to ticket'}
              </Chip>
            )}

            {/* Actions pushed to the right */}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {/* When the quote was created from a job, surface a quick link
                  back to that job (opens the /ticket/<number> view). Hidden
                  for a quote that GENERATED a ticket — the "Converted to
                  ticket" chip already links there. */}
              {!isTicketGenerated && quote.sm4rtJobId && quote.rsTicketNumber && (
                <Tooltip title="Open the job this quote was created from">
                  <Button
                    variant="outlined"
                    size="small"
                    sx={headerBtnSx}
                    onClick={() => navigate(`/ticket/${quote.rsTicketNumber}`)}
                  >
                    Job #{quote.rsTicketNumber}
                  </Button>
                </Tooltip>
              )}
              {/* Correct the customer's details (quick quotes often capture
                  incomplete info). Changes sync to RepairShopr. Admin-only,
                  and allowed even after the quote is locked. */}
              {isAdmin && quote.customerId && (
                <Tooltip title="Edit the customer's details">
                  <Button
                    variant="outlined"
                    size="small"
                    sx={headerBtnSx}
                    startIcon={<IconUserEdit size={16} stroke={1.5} />}
                    onClick={() => setEditCustomerOpen(true)}
                  >
                    Edit customer
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Search supplier offers across all vendors">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={headerBtnSx}
                    startIcon={<IconBuildingStore size={16} stroke={1.5} />}
                    onClick={() => setSupplierSearchOpen(true)}
                    disabled={isLocked}
                  >
                    Find Product
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Preview & copy HTML for Outlook">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={headerBtnSx}
                    startIcon={<IconCopy size={16} stroke={1.5} />}
                    onClick={handleOpenPreview}
                  >
                    Preview
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Variation / duplicate">
                <span>
                  <IconButton
                    ref={copyBtnRef}
                    size="small"
                    onClick={() => setCopyMenuOpen((o) => !o)}
                    disabled={copyBusy}
                  >
                    {copyBusy ? <CircularProgress size={18} /> : <IconDotsVertical size={18} stroke={1.5} />}
                  </IconButton>
                </span>
              </Tooltip>
              {copyMenuOpen && (
                <ActionMenu
                  anchorRef={copyBtnRef}
                  onClose={() => setCopyMenuOpen(false)}
                  options={[
                    {
                      label: 'Create variation',
                      description: 'Same customer',
                      Icon: IconArrowsSplit2,
                      onSelect: handleCreateVariation,
                    },
                    {
                      label: 'Duplicate…',
                      description: 'Different customer',
                      Icon: IconCopy,
                      onSelect: () => setDuplicateOpen(true),
                    },
                  ]}
                />
              )}
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<IconTrash size={16} stroke={1.5} />}
                onClick={handleDelete}
                sx={{ fontSize: 13, fontWeight: 500, py: 0.5, px: 1.25, minHeight: 0, '& .MuiButton-startIcon': { mr: 0.5 } }}
              >
                Delete
              </Button>
              {/* Convert: a quote becomes EITHER a ticket OR an invoice
                  (each terminal), or links to an invoice raised manually.
                  Hidden once the quote is locked. */}
              {!isLocked && (
                <>
                  <Button
                    ref={convertBtnRef}
                    variant="contained"
                    size="small"
                    startIcon={<IconReceipt size={16} stroke={1.5} />}
                    endIcon={<IconChevronDown size={16} stroke={1.5} />}
                    onClick={() => setConvertMenuOpen((o) => !o)}
                    disabled={saving}
                    sx={{ ...PRIMARY_BTN_SX, fontSize: 13, fontWeight: 600, py: 0.5, px: 1.5, minHeight: 0, '& .MuiButton-startIcon': { mr: 0.5 } }}
                  >
                    Convert
                  </Button>
                  {convertMenuOpen && (
                    <ActionMenu
                      anchorRef={convertBtnRef}
                      onClose={() => setConvertMenuOpen(false)}
                      options={[
                        ...(!isTicketLinked
                          ? ([
                              {
                                label: 'Generate ticket',
                                description: 'Create an RS ticket',
                                Icon: IconTicket,
                                onSelect: handleGenerateTicket,
                              },
                              {
                                label: 'Link existing ticket…',
                                description:
                                  'Bind an RS ticket raised elsewhere — no line items added',
                                Icon: IconLink,
                                onSelect: handleLinkTicket,
                              },
                            ] as ActionMenuOption[])
                          : []),
                        {
                          label: 'Generate invoice',
                          description: 'Create an RS invoice',
                          Icon: IconReceipt,
                          onSelect: handleGenerateInvoice,
                        },
                        {
                          label: 'Link existing invoice…',
                          description: 'Raised manually — no line items added',
                          Icon: IconLink,
                          onSelect: handleLinkInvoice,
                        },
                      ]}
                    />
                  )}
                </>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={saving || isLocked}
                sx={{ ...PRIMARY_BTN_SX, fontSize: 13, fontWeight: 600, py: 0.5, px: 1.5, minHeight: 0 }}
              >
                {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
              </Button>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ ...CARD_SX, p: 2 }}>
          <div className="quote-detail__card-title">Quote Details</div>
          <div className="quote-detail__field-row">
            <div className="quote-detail__field--grow">
              <FormField label="Title" disabled={isLocked}>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isLocked}
                />
              </FormField>
            </div>
            <div style={{ width: 200 }}>
              <SmartSelect
                label="Type"
                value={type}
                options={TYPE_OPTIONS}
                onChange={(v) => v && setType(v as QuoteType)}
                disabled={isLocked}
              />
            </div>
            <div style={{ width: 160 }}>
              <SmartSelect
                label="Status"
                value={status}
                options={STATUS_SELECT_OPTIONS}
                onChange={(v) => v && setStatus(v as QuoteStatus)}
              />
            </div>
            <div style={{ width: 180 }}>
              <DateField
                label="Valid until"
                value={validUntil || null}
                onChange={(iso) => setValidUntil(iso ?? '')}
                disabled={isLocked}
              />
            </div>
            <div style={{ width: 120 }}>
              <SmartSelect
                label="GST"
                value={priceDisplayMode}
                options={GST_SELECT_OPTIONS}
                onChange={(v) => v && setPriceDisplayMode(v as 'ex' | 'inc')}
                disabled={isLocked}
              />
            </div>
            {/* Markup% drives the supplier-picker's sell-from-cost
                calculation on new-computer quote types. Repair / m365 /
                legacy "replacement" quotes don't use the supplier picker
                in a markup-driven way (repair lines price from
                labour-hours, with parts billed at cost), so the field is
                hidden there to avoid suggesting it's wired up. */}
            {type.startsWith('new_') && (
              <div style={{ width: 120 }}>
                <FormField label="Markup %" disabled={isLocked}>
                  <input
                    inputMode="decimal"
                    value={markupPct}
                    onChange={(e) => setMarkupPct(e.target.value)}
                    disabled={isLocked}
                  />
                </FormField>
              </div>
            )}
            {isRepairQuote && (
              <div style={{ width: 150 }}>
                <SmartSelect
                  label="Device"
                  value={deviceType ?? ''}
                  options={DEVICE_SELECT_OPTIONS}
                  onChange={(v) => setDeviceType((v || null) as RepairDeviceType)}
                  disabled={isLocked}
                />
                <p className="quote-detail__hint">Filters the picker</p>
              </div>
            )}
          </div>

          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={3}
            sx={{ mt: 2, flexWrap: 'wrap' }}
          >
            {isCustomDesktop ? (
              <Typography
                sx={{ m: 0, height: 40, display: 'flex', alignItems: 'center', fontSize: 13, color: '#666' }}
              >
                Custom builds always show as one summary line on the quote and
                invoice.
              </Typography>
            ) : (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={consolidated}
                    onChange={(e) => setConsolidated(e.target.checked)}
                    disabled={isLocked}
                    sx={CHECKBOX_SX}
                  />
                }
                label="Consolidated — show as one summary line on the quote and invoice"
                sx={{ m: 0, height: 40, '& .MuiFormControlLabel-label': { fontSize: 13, color: '#333' } }}
              />
            )}
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeEftFee}
                    onChange={(e) => setIncludeEftFee(e.target.checked)}
                    disabled={isLocked}
                    sx={CHECKBOX_SX}
                  />
                }
                label="Include EFT"
                sx={{ m: 0, height: 40, '& .MuiFormControlLabel-label': { fontSize: 13, color: '#333' } }}
              />
              <div style={{ width: 110 }}>
                <FormField label="Rate %" disabled={isLocked || !includeEftFee}>
                  <input
                    inputMode="decimal"
                    value={eftFeeRatePct}
                    onChange={(e) => setEftFeeRatePct(e.target.value)}
                    disabled={isLocked || !includeEftFee}
                  />
                </FormField>
                <p className="quote-detail__hint">0 to disable</p>
              </div>
            </Stack>
          </Stack>

          {consolidated &&
            (isRepairQuote ? (
              // Repair quotes compose their customer-facing summary from the
              // job's diagnostics / check-in sources rather than from marked
              // items. The composed text flows up into consolidatedDescription.
              <JobSummaryEditor
                sm4rtJobId={quote.sm4rtJobId}
                sourceCheckInDraftJobId={sourceCheckInDraftJobId}
                disabled={isLocked}
                onComposedChange={setConsolidatedDescription}
              />
            ) : (
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="text"
                    onClick={() =>
                      setConsolidatedDescription(
                        isCustomDesktop
                          ? buildDesktopBuildDescription(items)
                          : buildConsolidatedDescription(items),
                      )
                    }
                    disabled={isLocked}
                  >
                    {isCustomDesktop ? 'Regenerate from build' : 'Regenerate from items'}
                  </Button>
                </Stack>
                <FormField label="Consolidated description" disabled={isLocked}>
                  <textarea
                    className="quote-detail__textarea"
                    rows={isCustomDesktop ? 6 : 3}
                    value={consolidatedDescription}
                    onChange={(e) => setConsolidatedDescription(e.target.value)}
                    placeholder={
                      (isCustomDesktop
                        ? buildDesktopBuildDescription(items)
                        : buildConsolidatedDescription(items)) || 'Repair Service'
                    }
                    disabled={isLocked}
                  />
                </FormField>
                <p className="quote-detail__hint">Shown on the quote and invoice.</p>
              </Stack>
            ))}

          <div style={{ marginTop: 16 }}>
            <FormField label="Notes" disabled={isLocked}>
              <textarea
                className="quote-detail__textarea"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isLocked}
                placeholder="Shown to the customer on the quote and invoice — e.g. delivery timeframes, what's included/excluded, payment terms."
              />
            </FormField>
          </div>

          <div style={{ marginTop: 16, maxWidth: 360 }}>
            <TermsTemplateSelect
              value={termsTemplateId}
              onChange={setTermsTemplateId}
              disabled={isLocked}
            />
          </div>
        </Box>

      {isRepairQuote && (
        <RepairQuoteDeviceDetails
          sm4rtJobId={quote.sm4rtJobId}
          sourceCheckInDraftJobId={sourceCheckInDraftJobId}
          onChangeSourceCheckInDraftJobId={setSourceCheckInDraftJobId}
          disabled={isLocked}
        />
      )}

        <Box sx={{ ...CARD_SX, p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
            {isRepairQuote
              ? 'Repair services'
              : isServiceShaped
                ? 'Services'
                : isCustomDesktop
                  ? 'Build'
                  : 'Items'}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <SendToOrderingButton quote={quote} vendors={vendors} />
            <Typography sx={{ fontSize: 13, color: '#666' }}>
              {isServiceShaped ? 'Quote total' : 'Marked total'}{' '}
              ({priceDisplayMode === 'inc' ? 'inc' : 'ex'} GST
              {includeEftFee && eftFeeRate > 0 ? ', incl. EFT' : ''}):{' '}
              <strong style={{ color: '#1a1a1a' }}>{fmtCurrency(total)}</strong>
            </Typography>
          </Stack>
        </Stack>
        {isServiceShaped ? (
          <Stack spacing={2}>
            <RepairServicesEditor
              services={repairServices}
              deviceType={deviceType}
              priceDisplayMode={priceDisplayMode}
              includeEftFee={includeEftFee}
              eftFeeRate={eftFeeRate}
              vendors={vendors}
              onChange={setRepairServices}
              // First-service-wins, fill-if-empty: only adopt the added
              // service's default terms template when the quote hasn't got
              // one yet. Functional update reads the live value so later
              // services and manual edits never get clobbered.
              onServiceDefaultTermsTemplate={(slug) =>
                setTermsTemplateId((current) => current || slug)
              }
              disabled={isLocked}
              // Network installs always bill one fixed "Network Installation"
              // line — lock the editor to RCS.NetworkInstall so the tech
              // isn't asked to pick a device-scoped repair service.
              lockedServiceCode={
                type === 'network_install'
                  ? RCS_NETWORK_INSTALL_SKU
                  : undefined
              }
              lockedServiceName={
                type === 'network_install' ? 'Network Installation' : undefined
              }
            />
            {/* Diagnostic-fee reimbursement — auto-derived server-side
                from the source check-in's invoice. Sits inline with the
                Bundle discount line because it's effectively the same
                shape: a single negative discount line that flows through
                to the invoice (as DISC.Diags). Customer-paid amount is
                already inc-GST; rendered here as a read-only credit
                rather than an editable field. */}
            {diagReimbursement && (
              <Box sx={{ p: 2, borderRadius: '8px', border: '0.5px solid #b6e2d3', background: '#f4fbf8' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                      Diagnostics fee reimbursement
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#888', mt: 0.25 }}>
                      Auto-derived from check-in invoice{' '}
                      {diagReimbursement.sourceInvoiceNumber}. Appears as
                      a DISC.Diags negative line on the resulting invoice
                      if the customer proceeds.
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: 14, color: '#0D7A56' }}
                  >
                    {fmtCurrency(-diagReimbursement.amount)} inc GST
                  </Typography>
                </Stack>
              </Box>
            )}
            <Box sx={{ p: 2, borderRadius: '8px', border: '0.5px solid #e2eaf3' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={{ minWidth: 140, fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                  Bundle discount
                </Typography>
                <div style={{ width: 160 }}>
                  <FormField label="Amount ex-GST" disabled={isLocked}>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={bundleDiscount?.amount ?? ''}
                      onChange={(e) => {
                        const amount = Number(e.target.value);
                        if (!Number.isFinite(amount) || amount <= 0) {
                          setBundleDiscount(null);
                          return;
                        }
                        setBundleDiscount({
                          amount,
                          description: bundleDiscount?.description || 'Bundle discount',
                        });
                      }}
                      disabled={isLocked}
                      style={{ textAlign: 'right' }}
                    />
                  </FormField>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <FormField label="Description" disabled={isLocked || !bundleDiscount}>
                    <input
                      value={bundleDiscount?.description ?? ''}
                      onChange={(e) =>
                        setBundleDiscount(
                          bundleDiscount
                            ? { ...bundleDiscount, description: e.target.value }
                            : null,
                        )
                      }
                      disabled={isLocked || !bundleDiscount}
                      placeholder="Multi-service discount"
                    />
                  </FormField>
                </div>
              </Stack>
              <Typography sx={{ display: 'block', mt: 1, fontSize: 11, color: '#888' }}>
                Leave amount empty for no discount. Shown as one negative
                line on the invoice when set.
              </Typography>
            </Box>
          </Stack>
        ) : isCustomDesktop ? (
          <DesktopBuildEditor
            items={items}
            onChange={setItems}
            disabled={isLocked}
            priceDisplayMode={priceDisplayMode}
            markup={(() => {
              const parsed = Number(markupPct);
              return Number.isFinite(parsed) ? parsed / 100 : 0.115;
            })()}
          />
        ) : (
          <QuoteEditor
            items={items}
            onChange={setItems}
            disabled={isLocked}
            quoteType={type}
            priceDisplayMode={priceDisplayMode}
            markup={(() => {
              const parsed = Number(markupPct);
              return Number.isFinite(parsed) ? parsed / 100 : 0.115;
            })()}
          />
        )}
        </Box>
      </Box>

      <QuoteHtmlPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        quote={{
          ...quote,
          items,
          repairServices,
          bundleDiscount,
          deviceType,
          sourceCheckInDraftJobId,
          includeEftFee,
          eftFeeRate,
          name,
          note: note || null,
          validUntil: validUntil || null,
          status,
          type,
          consolidated,
          consolidatedDescription: consolidatedDescription || null,
          priceDisplayMode,
          markup: (() => {
            const parsed = Number(markupPct);
            return Number.isFinite(parsed) ? parsed / 100 : 0.115;
          })(),
        }}
      />

      <GenerateInvoiceDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        quote={{
          ...quote,
          items,
          repairServices,
          bundleDiscount,
          deviceType,
          sourceCheckInDraftJobId,
          includeEftFee,
          eftFeeRate,
        }}
        onPromoted={() => load()}
      />

      <GenerateTicketDialog
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        quote={{
          ...quote,
          items,
          repairServices,
          bundleDiscount,
          deviceType,
          sourceCheckInDraftJobId,
          includeEftFee,
          eftFeeRate,
        }}
        onGenerated={() => load()}
      />

      <LinkInvoiceDialog
        open={linkInvoiceOpen}
        onClose={() => setLinkInvoiceOpen(false)}
        quote={quote}
        onLinked={() => load()}
      />

      <LinkTicketDialog
        open={linkTicketOpen}
        onClose={() => setLinkTicketOpen(false)}
        quote={quote}
        onLinked={() => load()}
      />

      <EditCustomerDialog
        open={editCustomerOpen}
        onClose={() => setEditCustomerOpen(false)}
        customerId={quote.customerId}
        onSaved={() => load()}
      />

      <DistiListingSearchDialog
        open={supplierSearchOpen}
        onClose={() => setSupplierSearchOpen(false)}
        priceDisplayMode={priceDisplayMode}
        markup={(() => {
          const parsed = Number(markupPct);
          return Number.isFinite(parsed) ? parsed / 100 : 0.115;
        })()}
        onPick={(offer) => {
          // Global supplier search isn't scoped to an existing item-group,
          // so each pick creates a new item with the offer as its first
          // option. The tech can rename / merge the items afterwards.
          const newItemId =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
          const newOptionId =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
          setItems([
            ...items,
            {
              id: newItemId,
              name: offer.productCode || offer.description.slice(0, 60),
              position: items.length,
              selectedOptionId: newOptionId,
              markedForInvoice: false,
              options: [
                {
                  id: newOptionId,
                  description: offer.description,
                  buyPriceExGst: offer.costExGst,
                  shipping: offer.shippingExGst,
                  url: offer.distiUrl,
                  sellPrice: offer.priceUnknown ? 0 : offer.sellPriceExGst,
                  productCode: offer.productCode || '',
                  imageUrl: offer.imageUrl,
                  sourceDistiId: offer.distiId,
                  sourceDistiSku: offer.distiSku,
                  sourceSpecs: offer.specs,
                },
              ],
            },
          ]);
          // Dialog stays open after a pick — the tech can add more
          // supplier items in one session and dismisses via the
          // "Back to quote" button in the picker.
        }}
      />

      <DuplicateQuoteDialog
        source={duplicateOpen ? { number: quote.number } : null}
        onClose={() => setDuplicateOpen(false)}
        onDuplicated={(newNumber) => {
          setDuplicateOpen(false);
          navigate(`/quote/${newNumber}`);
        }}
      />
    </Box>
  );
};

export default QuoteDetailPage;

// ---------------------------------------------------------------------------
// Terms template Select — pulls from the catalog hook so it lives outside
// the (already-large) main component body.
// ---------------------------------------------------------------------------

interface TermsTemplateSelectProps {
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
}

const SENTINEL_DEFAULT = '__default__';

const TermsTemplateSelect: React.FC<TermsTemplateSelectProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const { templates, isLoading } = useQuoteTermsTemplates();
  const defaultTemplate = templates.find((t) => t.isDefault) || null;

  const options: SmartSelectOption[] = [
    {
      value: SENTINEL_DEFAULT,
      label: `Default${defaultTemplate ? ` (${defaultTemplate.name})` : ''}`,
    },
    ...templates.map((t) => ({ value: t.slug, label: t.name })),
  ];

  return (
    <>
      <SmartSelect
        label="Terms & Conditions template"
        value={value ?? SENTINEL_DEFAULT}
        options={options}
        onChange={(v) => onChange(!v || v === SENTINEL_DEFAULT ? null : v)}
        disabled={disabled || isLoading}
      />
      {value === null && (
        <p className="quote-detail__hint">
          Renders with whichever template is currently default
        </p>
      )}
    </>
  );
};
